import {
  PortableText as PortableTextBase,
  type PortableTextComponents,
} from '@portabletext/react'
import type {TypedObject} from '@portabletext/types'
import {isRichText, plainText, type RichTextValue} from '@/lib/richtext'

/**
 * Inline marks shared by every rich-text field. These mirror the Studio's
 * `basicMarks` (bold / italic / underline / strike-through / link).
 */
export const inlineMarks: PortableTextComponents['marks'] = {
  strong: ({children}) => <strong>{children}</strong>,
  em: ({children}) => <em>{children}</em>,
  underline: ({children}) => <u>{children}</u>,
  'strike-through': ({children}) => <s>{children}</s>,
  link: ({children, value}) => {
    const href = (value as {href?: string})?.href || '#'
    const external = /^https?:\/\//.test(href)
    return (
      <a href={href} {...(external ? {target: '_blank', rel: 'noopener noreferrer'} : {})}>
        {children}
      </a>
    )
  },
}

/** Split legacy plain-text content into paragraphs on blank lines. */
function paragraphs(text: string) {
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
}

/**
 * Renders a `richText` field as paragraphs, applying `className` to each one so
 * existing CSS (`.blurb`, `.lead`, …) keeps working.
 *
 * Also accepts a plain string — both for content authored before the rich-text
 * migration and for copy hard-coded in a page (e.g. the ContactBand default).
 */
export function RichText({
  value,
  className,
}: {
  value: RichTextValue
  className?: string
}) {
  if (!value) return null

  if (typeof value === 'string') {
    const paras = paragraphs(value)
    if (paras.length === 0) return null
    return (
      <>
        {paras.map((p, i) => (
          <p className={className} key={i}>
            {p}
          </p>
        ))}
      </>
    )
  }

  if (value.length === 0) return null

  return (
    <PortableTextBase
      value={value as TypedObject[]}
      components={{
        block: {normal: ({children}) => <p className={className}>{children}</p>},
        marks: inlineMarks,
      }}
    />
  )
}

/**
 * Renders a `richText` field without any block wrapper — for copy that sits
 * inside markup the page already owns (a quote between “ ”, a sentence with a
 * trailing span). Multiple paragraphs are joined with a space.
 */
export function RichTextInline({value}: {value: RichTextValue}) {
  if (!value) return null
  if (typeof value === 'string') return <>{value}</>
  if (value.length === 0) return null

  return (
    <PortableTextBase
      value={value as TypedObject[]}
      components={{
        block: {
          normal: ({children, index}) => (
            <>
              {index > 0 ? ' ' : null}
              {children}
            </>
          ),
        },
        marks: inlineMarks,
      }}
    />
  )
}

export {isRichText, plainText}
export type {RichTextValue}
